import { useEffect, useState } from "react";
import { apiPost, prettifyApiError } from "@/shared/api";

/** Le 6 causali di chiamata (valore stabile lato DB · etichetta italiana). */
const CALL_REASONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "info_spedizione", label: "Info spedizione" },
  { value: "reclamo", label: "Reclamo" },
  { value: "reso_sostituzione", label: "Reso/Sostituzione" },
  { value: "info_prodotto", label: "Info prodotto" },
  { value: "sollecito_pagamento", label: "Sollecito pagamento" },
  { value: "altro", label: "Altro" },
];

interface CallLogDialogProps {
  order: {
    reference: string;
    customerName?: string;
    customerEmail?: string;
    customerPhone?: string;
  };
  onClose: () => void;
  onSaved?: () => void;
}

/**
 * Registra una chiamata al cliente (CRM): causale obbligatoria + nota
 * opzionale → POST /api/crm/calls. Modale piccola, stesso stile degli altri
 * dialog della card (RiordinaSostDialog): backdrop, ESC per chiudere,
 * ``data-no-open`` cosi' il click non naviga alla pagina ordine.
 */
export function CallLogDialog({ order, onClose, onSaved }: CallLogDialogProps) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ESC per annullare (allineato a RiordinaSostDialog). Ignorato durante l'invio.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const canSubmit = reason !== "" && !busy;

  async function handleSubmit() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await apiPost("/api/crm/calls", {
        order_ref: order.reference,
        customer_email: order.customerEmail ?? null,
        customer_name: order.customerName ?? null,
        reason,
        note: note.trim() || null,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Registra chiamata cliente"
      data-no-open
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-line bg-card p-5 shadow-popover">
        <h3 className="font-serif text-[16px] font-medium text-ink">Registra chiamata</h3>
        <p className="mt-1 text-[12px] text-ink-3">
          {order.customerName || "Cliente"}
          {order.customerPhone && (
            <>
              {" · "}
              <a
                href={`tel:${order.customerPhone.replace(/\s+/g, "")}`}
                className="font-mono text-ink-2 hover:text-accent"
              >
                {order.customerPhone}
              </a>
            </>
          )}
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-2">
            Motivo della chiamata
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
              autoFocus
              aria-label="Motivo della chiamata"
              className="h-[44px] rounded-md border border-line bg-card px-3 text-[13px] font-normal text-ink outline-none focus:border-accent disabled:opacity-50"
            >
              <option value="" disabled>
                Seleziona…
              </option>
              {CALL_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium text-ink-2">
            Note (opzionale)
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={busy}
              rows={3}
              className="rounded-md border border-line bg-card px-3 py-2 text-[13px] font-normal text-ink outline-none focus:border-accent disabled:opacity-50 resize-y"
              placeholder="Es. cliente richiede consegna al piano…"
            />
          </label>
        </div>

        {error && (
          <p role="alert" className="mt-3 text-[12px] text-red">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-line bg-card px-3 py-2 text-[12px] font-medium text-ink-2 hover:border-ink-4 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
            className="rounded-md border border-teal bg-teal px-3 py-2 text-[12px] font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "Salvo…" : "Salva chiamata"}
          </button>
        </div>
      </div>
    </div>
  );
}
